import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CabinetShell from './CabinetShell';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Loader2, Upload, ShieldAlert, X } from 'lucide-react';
import { toast } from 'sonner';
import { UploadButtons } from './UploadButtons';
import { prepareFiles } from '../../lib/imageCompress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACCEPT = '.stl,.ply,.obj,.zip,.pdf,.jpg,.jpeg,.png';

export default function OrderNew() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    praticien_name: '', patient_internal_ref: '', patient_last_name: '',
    product_id: '', teeth: '', shade: '', material: '', scanner_used: '',
    deadline_requested: '', urgent: false, comments: '',
  });

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => toast.error('Erreur chargement des types de travaux'));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (draft) => {
    if (!form.praticien_name || !form.patient_internal_ref || !form.product_id) {
      toast.error('Praticien, identifiant patient et type de travail sont requis');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        teeth: form.teeth.split(/[\s,;]+/).filter(Boolean),
        deadline_requested: form.deadline_requested || null,
        draft,
      };
      const { data: order } = await axios.post(`${API}/orders`, payload);
      if (files.length > 0) {
        const prepared = await prepareFiles(files);
        const fd = new FormData();
        prepared.forEach((f) => fd.append('files', f));
        await axios.post(`${API}/orders/${order.id}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success(`Commande ${order.order_number} ${draft ? 'enregistrée en brouillon' : 'envoyée'}`);
      navigate(`/orders/${order.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <CabinetShell title="Nouvelle commande">
      <Card className="border-border">
        <CardHeader><CardTitle className="text-base">Informations du dossier</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="praticien">Praticien *</Label>
              <Input id="praticien" value={form.praticien_name} onChange={(e) => set('praticien_name', e.target.value)} placeholder="Dr Martin" data-testid="order-praticien-input" />
            </div>
            <div>
              <Label htmlFor="patref">Identifiant patient interne *</Label>
              <Input id="patref" value={form.patient_internal_ref} onChange={(e) => set('patient_internal_ref', e.target.value)} placeholder="PAT-0042" data-testid="order-patient-ref-input" />
            </div>
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex gap-2 text-sm text-amber-800">
            <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5" />
            <p>N'indiquez pas de données patient nominatives sauf nécessité. Le nom (sans prénom) suffit pour identifier le dossier.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="patname">Nom du patient (optionnel, sans prénom)</Label>
              <Input id="patname" value={form.patient_last_name} onChange={(e) => set('patient_last_name', e.target.value)} placeholder="Dupont" data-testid="order-patient-name-input" />
            </div>
            <div>
              <Label>Type de travail *</Label>
              <Select value={form.product_id} onValueChange={(v) => set('product_id', v)}>
                <SelectTrigger data-testid="order-product-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.label_fr}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="teeth">Dent(s) concernée(s)</Label>
              <Input id="teeth" value={form.teeth} onChange={(e) => set('teeth', e.target.value)} placeholder="16, 17" data-testid="order-teeth-input" />
            </div>
            <div>
              <Label htmlFor="shade">Teinte</Label>
              <Input id="shade" value={form.shade} onChange={(e) => set('shade', e.target.value)} placeholder="A2" data-testid="order-shade-input" />
            </div>
            <div>
              <Label htmlFor="material">Matériau</Label>
              <Input id="material" value={form.material} onChange={(e) => set('material', e.target.value)} placeholder="Zircone multicouche" data-testid="order-material-input" />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="scanner">Scanner utilisé</Label>
              <Input id="scanner" value={form.scanner_used} onChange={(e) => set('scanner_used', e.target.value)} placeholder="Medit i700, iTero..." data-testid="order-scanner-input" />
            </div>
            <div>
              <Label htmlFor="deadline">Délai souhaité</Label>
              <Input id="deadline" type="date" value={form.deadline_requested} onChange={(e) => set('deadline_requested', e.target.value)} data-testid="order-deadline-input" />
            </div>
            <div className="flex items-end pb-2 gap-2">
              <Checkbox id="urgent" checked={form.urgent} onCheckedChange={(v) => set('urgent', !!v)} data-testid="order-urgent-checkbox" />
              <Label htmlFor="urgent" className="cursor-pointer">Urgent</Label>
            </div>
          </div>
          <div>
            <Label htmlFor="comments">Commentaires</Label>
            <Textarea id="comments" value={form.comments} onChange={(e) => set('comments', e.target.value)} rows={3} data-testid="order-comments-input" />
          </div>
          <div>
            <Label>Fichiers d'empreintes (STL, PLY, OBJ, ZIP, PDF, photos)</Label>
            <label
              className="mt-1 flex flex-col items-center justify-center gap-2 border-2 border-dashed border-border rounded-lg p-6 cursor-pointer hover:border-primary/50 transition-colors"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); setFiles((f) => [...f, ...Array.from(e.dataTransfer.files)]); }}
              data-testid="order-file-dropzone"
            >
              <Upload className="h-6 w-6 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Glissez-déposez ou cliquez pour sélectionner</span>
              <input type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => setFiles((f) => [...f, ...Array.from(e.target.files)])} />
            </label>
            <div className="mt-2">
              <UploadButtons onFiles={(list) => setFiles((f) => [...f, ...Array.from(list)])} disabled={saving} />
            </div>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-sm bg-secondary/50 rounded px-3 py-1.5">
                    <span className="truncate">{f.name}</span>
                    <button onClick={() => setFiles((arr) => arr.filter((_, j) => j !== i))} data-testid={`remove-file-${i}`}><X className="h-4 w-4 text-muted-foreground" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" disabled={saving} onClick={() => submit(true)} data-testid="order-save-draft-btn">Enregistrer en brouillon</Button>
            <Button disabled={saving} onClick={() => submit(false)} data-testid="order-submit-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Envoyer la commande
            </Button>
          </div>
        </CardContent>
      </Card>
    </CabinetShell>
  );
}
